import React, { useState, useEffect } from 'react';
import { useToast } from './ToastContainer';
import apiService from '../services/api';

const TimelineEstados = ({ contenedor, onEstadoCambiado }) => {
  const toast = useToast();
  const [estados, setEstados] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [estadoSeleccionado, setEstadoSeleccionado] = useState('');
  const [ubicacion, setUbicacion] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [cargando, setCargando] = useState(false);
  const [contenedorActual, setContenedorActual] = useState(contenedor); // ⬅️ NUEVO

  // ACTUALIZAR CONTENEDOR ACTUAL CUANDO CAMBIE EL PROP
  useEffect(() => {
    setContenedorActual(contenedor);
  }, [contenedor]);

  useEffect(() => {
    cargarEstados();
    if (contenedor?.id) {
      cargarTimeline();
    }
  }, [contenedor?.id]);

  const cargarEstados = async () => {
    try {
      const data = await apiService.get('/estados/catalogo');
      setEstados(data);
    } catch (error) {
      console.error('Error al cargar estados:', error);
    }
  };

  const cargarTimeline = async () => {
    try {
      const data = await apiService.get(`/contenedores/${contenedor.id}/timeline`);
      setTimeline(data);
      console.log('Timeline cargado:', data);
    } catch (error) {
      console.error('Error al cargar timeline:', error);
    }
  };

  const cambiarEstado = async () => {
    if (!estadoSeleccionado) {
      toast.warning('Selecciona un estado');
      return;
    }

    setCargando(true);

    try {
      const data = await apiService.post(
        `/contenedores/${contenedor.id}/cambiar-estado`,
        {
          estadoNuevo: estadoSeleccionado,
          ubicacion,
          observaciones,
          usuarioId: JSON.parse(localStorage.getItem('user') || '{}').id || 1
        }
      );

      if (data.success) {
        toast.success('Estado actualizado correctamente');
        setMostrarModal(false);
        setEstadoSeleccionado('');
        setUbicacion('');
        setObservaciones('');

        // ACTUALIZAR CONTENEDOR ACTUAL INMEDIATAMENTE
        setContenedorActual(data.contenedor);

        // Recargar timeline
        await cargarTimeline();

        // Notificar al padre
        if (onEstadoCambiado) {
          onEstadoCambiado(data.contenedor);
        }
      } else {
        toast.error(data?.message || 'Error al cambiar estado');
      }
    } catch (error) {
      console.error('Error:', error);
      if (error.message !== 'Sesión expirada. Por favor, vuelve a iniciar sesión.') {
        toast.error('Error al cambiar estado');
      }
    } finally {
      setCargando(false);
    }
  };

  // DETECTAR SI ES URL O EMOJI (con fallback si viene "?" desde BD)
  const renderIcono = (icono, codigo = '', nombre = '', size = 'w-8 h-8') => {
    const iconoNormalizado = (icono || '').toString().trim();
    const codigoNormalizado = (codigo || '').toString().trim().toUpperCase();
    const nombreNormalizado = (nombre || '').toString().trim().toLowerCase();

    const iconosFallback = {
      RESERVADO: '📦',
      TRANSITO: '🚚',
      'EN TRANSITO': '🚚',
      PUERTO: '⚓',
      ADUANA: '🛃',
      LIBERADO: '✅',
      ENTREGA: '📍',
      ENTREGADO: '✔️',
      DEVUELTO: '↩️',
      CANCELADO: '❌',
    };

    const obtenerFallback = () => {
      if (codigoNormalizado && iconosFallback[codigoNormalizado]) return iconosFallback[codigoNormalizado];
      if (nombreNormalizado.includes('reserv')) return iconosFallback.RESERVADO;
      if (nombreNormalizado.includes('tránsito') || nombreNormalizado.includes('transito')) return iconosFallback.TRANSITO;
      if (nombreNormalizado.includes('puerto')) return iconosFallback.PUERTO;
      if (nombreNormalizado.includes('aduana')) return iconosFallback.ADUANA;
      if (nombreNormalizado.includes('liber')) return iconosFallback.LIBERADO;
      if (nombreNormalizado.includes('entrega') && !nombreNormalizado.includes('entregado')) return iconosFallback.ENTREGA;
      if (nombreNormalizado.includes('entregado')) return iconosFallback.ENTREGADO;
      if (nombreNormalizado.includes('devuelt')) return iconosFallback.DEVUELTO;
      if (nombreNormalizado.includes('cancel')) return iconosFallback.CANCELADO;
      return '';
    };

    const iconoFinal = !iconoNormalizado || iconoNormalizado === '?' ? obtenerFallback() : iconoNormalizado;

    if (!iconoFinal) return null;

    if (iconoFinal.startsWith('http')) {
      return <img src={iconoFinal} alt="Estado" className={size} />;
    }

    return <span style={{ fontSize: size === 'w-8 h-8' ? '28px' : '24px' }}>{iconoFinal}</span>;
  };

  const obtenerEstadosCompletados = () => {
    if (!timeline || timeline.length === 0) return [];
    return timeline.map(t => t.estadoNuevo);
  };

  const estadosCompletados = obtenerEstadosCompletados();

  const ordenPorCodigo = React.useMemo(() => {
    const map = new Map();
    estados.forEach((estado, index) => {
      const orden = Number.isFinite(Number(estado.orden)) ? Number(estado.orden) : index;
      map.set(estado.codigo, orden);
    });
    return map;
  }, [estados]);

  const estadoPorCodigo = React.useMemo(() => {
    const map = new Map();
    estados.forEach((estado) => {
      map.set(estado.codigo, estado);
    });
    return map;
  }, [estados]);

  const obtenerOrden = (codigo) => {
    if (!codigo) return null;
    const orden = ordenPorCodigo.get(codigo);
    return typeof orden === 'number' ? orden : null;
  };

  const obtenerNombreEstado = (codigo) => {
    if (!codigo) return '';
    return estadoPorCodigo.get(codigo)?.nombre || codigo;
  };

  const obtenerIconoEstado = (codigo) => {
    if (!codigo) return '';
    return estadoPorCodigo.get(codigo)?.icono || '';
  };

  const obtenerTipoCambio = (estadoAnterior, estadoNuevo) => {
    if (!estadoAnterior) return 'inicio';
    if (estadoAnterior === estadoNuevo) return 'repetido';

    const ordenAnterior = obtenerOrden(estadoAnterior);
    const ordenNuevo = obtenerOrden(estadoNuevo);

    if (ordenAnterior === null || ordenNuevo === null) return 'cambio';
    if (ordenNuevo > ordenAnterior) return 'avance';
    if (ordenNuevo < ordenAnterior) return 'retroceso';
    return 'cambio';
  };

  const formatearFechaCambio = (fecha) => {
    try {
      return new Date(fecha).toLocaleString('es-MX', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return fecha || '';
    }
  };

  const estadosVisitados = React.useMemo(() => {
    const set = new Set();
    timeline.forEach((t) => {
      if (t.estadoAnterior) set.add(t.estadoAnterior);
      if (t.estadoNuevo) set.add(t.estadoNuevo);
    });
    return set;
  }, [timeline]);

  const codigoEstadoActual = contenedorActual?.estadoActual || '';
  const ordenEstadoActual = obtenerOrden(codigoEstadoActual);
  const opcionesRetroceso = estados
    .filter((e) => e.codigo && e.codigo !== codigoEstadoActual)
    .filter((e) => (ordenEstadoActual === null ? false : obtenerOrden(e.codigo) !== null && obtenerOrden(e.codigo) < ordenEstadoActual))
    .filter((e) => estadosVisitados.has(e.codigo));
  const opcionesAvance = estados
    .filter((e) => e.codigo && e.codigo !== codigoEstadoActual)
    .filter((e) => (ordenEstadoActual === null ? true : obtenerOrden(e.codigo) !== null && obtenerOrden(e.codigo) > ordenEstadoActual));

  const iconoTexto = (icono, codigo, nombre) => {
    const normalizado = (icono || '').toString().trim();
    if (normalizado && !normalizado.startsWith('http') && normalizado !== '?') return `${normalizado} `;
    const fallback = (obtenerIconoEstado(codigo) || '').toString().trim();
    if (fallback && !fallback.startsWith('http') && fallback !== '?') return `${fallback} `;
    if (codigo || nombre) {
      // Reutiliza la misma lógica de fallback del renderer (solo para texto en <option>)
      const codigoNormalizado = (codigo || '').toString().trim().toUpperCase();
      const nombreNormalizado = (nombre || '').toString().trim().toLowerCase();
      const iconosFallback = {
        RESERVADO: '📦',
        TRANSITO: '🚚',
        'EN TRANSITO': '🚚',
        PUERTO: '⚓',
        ADUANA: '🛃',
        LIBERADO: '✅',
        ENTREGA: '📍',
        ENTREGADO: '✔️',
        DEVUELTO: '↩️',
        CANCELADO: '❌',
      };
      if (codigoNormalizado && iconosFallback[codigoNormalizado]) return `${iconosFallback[codigoNormalizado]} `;
      if (nombreNormalizado.includes('reserv')) return `${iconosFallback.RESERVADO} `;
      if (nombreNormalizado.includes('tránsito') || nombreNormalizado.includes('transito')) return `${iconosFallback.TRANSITO} `;
      if (nombreNormalizado.includes('puerto')) return `${iconosFallback.PUERTO} `;
      if (nombreNormalizado.includes('aduana')) return `${iconosFallback.ADUANA} `;
      if (nombreNormalizado.includes('liber')) return `${iconosFallback.LIBERADO} `;
      if (nombreNormalizado.includes('entrega') && !nombreNormalizado.includes('entregado')) return `${iconosFallback.ENTREGA} `;
      if (nombreNormalizado.includes('entregado')) return `${iconosFallback.ENTREGADO} `;
      if (nombreNormalizado.includes('devuelt')) return `${iconosFallback.DEVUELTO} `;
      if (nombreNormalizado.includes('cancel')) return `${iconosFallback.CANCELADO} `;
    }
    return '';
  };

  return (
    <div className="space-y-4">
      {/* TIMELINE HORIZONTAL */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Estado del Contenedor
          </h3>
          <button
            onClick={() => setMostrarModal(true)}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 
                     transition-colors font-medium text-sm shadow-md"
          >
            Cambiar Estado
          </button>
        </div>

        {/* Timeline Visual */}
        <div className="relative py-4">
          <div className="flex items-center justify-between">
            {estados.map((estado, index) => {
              const estaCompleto = estadosCompletados.includes(estado.codigo);
              const esActual = estado.codigo === contenedorActual?.estadoActual; 

              return (
                <div key={estado.id} className="flex items-center flex-1">
                  <div className="relative flex flex-col items-center">
                    <div
                      className={`
                        w-14 h-14 rounded-full flex items-center justify-center
                        transition-all duration-500 bg-white shadow-lg
                        ${esActual 
                          ? 'ring-4 ring-emerald-500 scale-125 animate-pulse' 
                          : estaCompleto 
                            ? 'ring-4 ring-emerald-400' 
                            : 'ring-2 ring-gray-300'
                        }
                      `}
                    >
                      {renderIcono(estado.icono, estado.codigo, estado.nombre)}
                    </div>
                    
                    {estaCompleto && !esActual && (
                      <div className="absolute -top-1 -right-1 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center shadow-lg z-10">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}

                    {esActual && (
                      <div className="absolute -top-2 -right-2 w-7 h-7 bg-emerald-600 rounded-full flex items-center justify-center shadow-lg animate-bounce z-10">
                        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                    
                    <div className="absolute -bottom-10 w-28 text-center">
                      <span className={`
                        text-xs font-semibold
                        ${esActual 
                          ? 'text-emerald-700 font-bold' 
                          : estaCompleto 
                            ? 'text-emerald-600' 
                            : 'text-gray-500'
                        }
                      `}>
                        {estado.nombre}
                      </span>
                    </div>
                  </div>

                  {index < estados.length - 1 && (
                    <div className="flex-1 h-2 mx-2 rounded-full overflow-hidden">
                      <div
                        className={`
                          h-full transition-all duration-500
                          ${estaCompleto 
                            ? 'bg-gradient-to-r from-emerald-400 to-emerald-500 shadow-md' 
                            : 'bg-gray-200'
                          }
                        `}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Estado actual */}
        {contenedorActual?.estadoActual && (
          <div className="mt-16 p-5 bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl border-2 border-emerald-300 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-lg ring-4 ring-emerald-500">
                {renderIcono(
                  contenedorActual.estadoIcono,
                  contenedorActual.estadoActual,
                  contenedorActual.estadoNombre,
                  'w-9 h-9'
                )}
              </div>
              <div className="flex-1">
                <p className="font-bold text-emerald-900 text-lg">
                  Estado Actual: {contenedorActual.estadoNombre || contenedorActual.estadoActual}
                </p>
                {contenedorActual?.ubicacionActual && (
                  <p className="text-sm text-emerald-700 mt-1 font-medium">
                   {contenedorActual.ubicacionActual}
                  </p>
                )}
                {contenedorActual?.observaciones && (
                  <p className="text-sm text-gray-600 mt-1">
                     {contenedorActual.observaciones}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* HISTORIAL */}
	      {timeline.length > 0 && (
	        <div className="bg-white rounded-lg shadow-sm p-6">
	          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Historial de Cambios
          </h4>
          
	          <div className="space-y-3">
	            {timeline.map((item, index) => {
	              const esUltimo = index === timeline.length - 1;
	              const tipoCambio = obtenerTipoCambio(item.estadoAnterior, item.estadoNuevo);
	              const nombreAnterior = item.estadoAnterior ? obtenerNombreEstado(item.estadoAnterior) : '';
	              const nombreNuevo = item.estadoNuevo ? obtenerNombreEstado(item.estadoNuevo) : (item.estadoNombre || '');
	              const badge =
	                tipoCambio === 'retroceso'
	                  ? { text: '⬅ Retrocedió', cls: 'bg-rose-100 text-rose-800 border-rose-200' }
	                  : tipoCambio === 'avance'
	                    ? { text: '➡ Avanzó', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' }
	                    : tipoCambio === 'repetido'
	                      ? { text: '⟲ Sin cambio', cls: 'bg-gray-100 text-gray-700 border-gray-200' }
	                      : tipoCambio === 'inicio'
	                        ? { text: 'Inicio', cls: 'bg-blue-50 text-blue-700 border-blue-100' }
	                        : { text: 'Cambio', cls: 'bg-gray-100 text-gray-700 border-gray-200' };
	              
	              return (
	                <div
	                  key={item.id}
                  className={`
                    flex gap-4 p-4 rounded-lg border-2 transition-all
                    ${esUltimo 
                      ? 'bg-emerald-50 border-emerald-300' 
                      : 'bg-gray-50 border-gray-200'
                    }
                  `}
                >
	                  <div className="flex-shrink-0">
	                    <div className={`
	                      w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-md
	                      ${esUltimo ? 'ring-4 ring-emerald-500' : 'ring-2 ring-emerald-400'}
	                    `}>
	                      {renderIcono(item.estadoIcono, item.estadoNuevo, item.estadoNombre, 'w-7 h-7')}
	                    </div>
	                  </div>
	                  
	                  <div className="flex-1">
	                    <div className="flex items-center justify-between mb-1">
	                      <div className="min-w-0">
	                        <div className="flex items-center gap-2 flex-wrap">
	                          <span className={`font-bold ${esUltimo ? 'text-emerald-900' : 'text-gray-900'}`}>
	                            {nombreNuevo}
	                          </span>
	                          <span className={`text-[11px] px-2 py-0.5 rounded-full border font-semibold ${badge.cls}`}>
	                            {badge.text}
	                          </span>
	                          {esUltimo && (
	                            <span className="text-[11px] px-2 py-0.5 rounded-full border font-semibold bg-emerald-50 text-emerald-800 border-emerald-200">
	                              Actual
	                            </span>
	                          )}
	                        </div>
	                        <p className="text-xs text-gray-600 mt-1">
	                          {tipoCambio === 'inicio' ? (
	                            <>Estado inicial</>
	                          ) : (
	                            <>
	                              De <span className="font-semibold">{nombreAnterior || item.estadoAnterior}</span> →{' '}
	                              <span className="font-semibold">{nombreNuevo || item.estadoNuevo}</span>
	                            </>
	                          )}
	                        </p>
	                      </div>
	                      <span className="text-xs text-gray-500 font-medium">
	                        {formatearFechaCambio(item.fechaCambio)}
	                      </span>
	                    </div>
	                    
	                    {item.ubicacion && (
	                      <p className="text-sm text-gray-700 mt-1">
	                         📍 {item.ubicacion}
	                      </p>
	                    )}
	                    
	                    {item.observaciones && (
	                      <p className="text-sm text-gray-700 mt-1">
	                         📝 {item.observaciones}
	                      </p>
	                    )}
	                    
	                    {item.cambiadoPor && (
	                      <p className="text-xs text-gray-500 mt-2 font-medium">
	                         👤 {item.cambiadoPor}
	                      </p>
	                    )}
	                  </div>
	                </div>
	              );
	            })}
          </div>
        </div>
      )}

      {/* MODAL */}
      {mostrarModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Cambiar Estado del Contenedor
              </h3>

	              <div className="mb-5">
	                <label className="block text-sm font-bold text-gray-700 mb-2">
	                  Nuevo Estado *
	                </label>
	                <select
	                  value={estadoSeleccionado}
	                  onChange={(e) => setEstadoSeleccionado(e.target.value)}
	                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 
	                           focus:ring-emerald-500 focus:border-emerald-500 font-medium"
	                >
	                  <option value="">Selecciona un estado...</option>
	                  {codigoEstadoActual && (
	                    <option value={codigoEstadoActual} disabled>
	                      {iconoTexto(contenedorActual?.estadoIcono, codigoEstadoActual, contenedorActual?.estadoNombre)}
	                      {contenedorActual?.estadoNombre || obtenerNombreEstado(codigoEstadoActual)} (Actual)
	                    </option>
	                  )}

	                  {opcionesRetroceso.length > 0 && (
	                    <optgroup label="Retroceder (estados anteriores)">
	                      {opcionesRetroceso.map((estado) => (
	                        <option key={estado.id} value={estado.codigo}>
	                          {iconoTexto(estado.icono, estado.codigo, estado.nombre)}
	                          {estado.nombre}
	                        </option>
	                      ))}
	                    </optgroup>
	                  )}

	                  {opcionesAvance.length > 0 && (
	                    <optgroup label="Avanzar (siguientes estados)">
	                      {opcionesAvance.map((estado) => (
	                        <option key={estado.id} value={estado.codigo}>
	                          {iconoTexto(estado.icono, estado.codigo, estado.nombre)}
	                          {estado.nombre}
	                        </option>
	                      ))}
	                    </optgroup>
	                  )}

	                  {ordenEstadoActual === null && (
	                    <optgroup label="Todos">
	                      {estados
	                        .filter((estado) => estado.codigo && estado.codigo !== codigoEstadoActual)
	                        .map((estado) => (
	                          <option key={estado.id} value={estado.codigo}>
	                            {iconoTexto(estado.icono, estado.codigo, estado.nombre)}
	                            {estado.nombre}
	                          </option>
	                        ))}
	                    </optgroup>
	                  )}
	                </select>
	                <p className="text-xs text-gray-500 mt-2">
	                  Tip: si eliges un estado en “Retroceder”, el historial marcará el movimiento como retroceso.
	                </p>
	              </div>

              <div className="mb-5">
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Ubicación Actual
                </label>
                <input
                  type="text"
                  value={ubicacion}
                  onChange={(e) => setUbicacion(e.target.value)}
                  placeholder="Ej: Puerto de Veracruz, Bodega 5..."
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 
                           focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Observaciones
                </label>
                <textarea
                  value={observaciones}
                  onChange={(e) => setObservaciones(e.target.value)}
                  placeholder="Notas adicionales..."
                  rows={3}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 
                           focus:ring-emerald-500 focus:border-emerald-500 resize-none"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setMostrarModal(false);
                    setEstadoSeleccionado('');
                    setUbicacion('');
                    setObservaciones('');
                  }}
                  className="flex-1 px-5 py-3 bg-gray-200 text-gray-800 rounded-lg 
                           hover:bg-gray-300 transition-colors font-bold shadow-md"
                  disabled={cargando}
                >
                  Cancelar
                </button>
                <button
                  onClick={cambiarEstado}
                  disabled={cargando}
                  className="flex-1 px-5 py-3 bg-emerald-600 text-white rounded-lg 
                           hover:bg-emerald-700 transition-colors font-bold shadow-md
                           disabled:bg-emerald-300 disabled:cursor-not-allowed"
                >
                  {cargando ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimelineEstados;
